export const DEFAULT_MONTHLY_TARGET = 80;

export const MONTH_LABELS = [
  { key: 1, label: 'Jan' },
  { key: 2, label: 'Feb' },
  { key: 3, label: 'Mar' },
  { key: 4, label: 'Apr' },
  { key: 5, label: 'May' },
  { key: 6, label: 'Jun' },
  { key: 7, label: 'Jul' },
  { key: 8, label: 'Aug' },
  { key: 9, label: 'Sep' },
  { key: 10, label: 'Oct' },
  { key: 11, label: 'Nov' },
  { key: 12, label: 'Dec' },
];

export const TARGET_CATEGORY = {
  OEE: 'oee',
  PRODUCTIVITY: 'productivity',
};

const LS_OEE_KEY = 'monthlyTargetsOEE';
const LS_PRODUCTIVITY_KEY = 'monthlyTargetsProductivity';

export function buildYearOptions(baseYear = new Date().getFullYear()) {
  const years = [];
  for (let y = baseYear - 5; y <= baseYear + 5; y += 1) {
    years.push(y);
  }
  return years;
}

export function createDefaultMonthValues(value = DEFAULT_MONTHLY_TARGET) {
  const months = {};
  for (let m = 1; m <= 12; m += 1) {
    months[m] = value;
  }
  return months;
}

function isMonthlyTargetMap(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeMonthValues(rawMonths, fallback = DEFAULT_MONTHLY_TARGET) {
  const months = createDefaultMonthValues(fallback);
  if (!rawMonths || typeof rawMonths !== 'object') return months;
  for (let m = 1; m <= 12; m += 1) {
    const key = String(m);
    const val = rawMonths[m] ?? rawMonths[key];
    const num = parseFloat(val);
    months[m] = Number.isFinite(num) ? num : fallback;
  }
  return months;
}

function parseLegacyScalar(raw, fallback = DEFAULT_MONTHLY_TARGET) {
  if (raw == null || raw === '') return fallback;
  const str = String(raw).trim();
  if (!str) return fallback;
  if (str.includes(',')) {
    const first = parseFloat(str.split(',')[0]);
    return Number.isFinite(first) ? first : fallback;
  }
  const num = parseFloat(str);
  return Number.isFinite(num) ? num : fallback;
}

export function parseMonthlyTargets(raw, fallback = DEFAULT_MONTHLY_TARGET) {
  if (raw == null || raw === '') {
    return {};
  }

  if (typeof raw === 'object') {
    if (isMonthlyTargetMap(raw)) {
      const hasYearKeys = Object.keys(raw).some((k) => /^\d{4}$/.test(String(k)));
      if (hasYearKeys) {
        const result = {};
        Object.keys(raw).forEach((yearKey) => {
          if (/^\d{4}$/.test(String(yearKey))) {
            result[yearKey] = normalizeMonthValues(raw[yearKey], fallback);
          }
        });
        return result;
      }
      const currentYear = String(new Date().getFullYear());
      return { [currentYear]: normalizeMonthValues(raw, fallback) };
    }
    return {};
  }

  const text = String(raw).trim();
  if (!text) return {};

  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      return parseMonthlyTargets(JSON.parse(text), fallback);
    } catch (e) {
      return {};
    }
  }

  const legacy = parseLegacyScalar(text, fallback);
  const currentYear = String(new Date().getFullYear());
  return { [currentYear]: createDefaultMonthValues(legacy) };
}

export function ensureYearMonths(data, year, fallback = DEFAULT_MONTHLY_TARGET) {
  const map = { ...(data || {}) };
  const yearKey = String(year);
  map[yearKey] = normalizeMonthValues(map[yearKey], fallback);
  return map;
}

export function getMonthTarget(data, year, month, fallback = DEFAULT_MONTHLY_TARGET) {
  const yearKey = String(year);
  const yearData = (data && data[yearKey]) || {};
  const val = yearData[month] ?? yearData[String(month)];
  const num = parseFloat(val);
  return Number.isFinite(num) ? num : fallback;
}

export function getMonthlyTargetArrayForYear(data, year, fallback = DEFAULT_MONTHLY_TARGET) {
  return MONTH_LABELS.map(({ key }) => getMonthTarget(data, year, key, fallback));
}

export function getCurrentMonthTarget(data, year = new Date().getFullYear(), fallback = DEFAULT_MONTHLY_TARGET) {
  const month = new Date().getMonth() + 1;
  return getMonthTarget(data, year, month, fallback);
}

export function loadMonthlyTargetsFromAccount(account) {
  const fallback = DEFAULT_MONTHLY_TARGET;
  const oee = parseMonthlyTargets(account && account.target1, fallback);
  const productivity = parseMonthlyTargets(account && account.target2, fallback);
  return { oee, productivity };
}

export function cacheMonthlyTargetsInLocalStorage(oee, productivity) {
  localStorage.setItem(LS_OEE_KEY, JSON.stringify(oee || {}));
  localStorage.setItem(LS_PRODUCTIVITY_KEY, JSON.stringify(productivity || {}));
  const currentOee = getCurrentMonthTarget(oee);
  const currentProductivity = getCurrentMonthTarget(productivity);
  localStorage.setItem('target1', `${currentOee},${currentProductivity}`);
}

export function readCachedMonthlyTargets() {
  let oee = {};
  let productivity = {};
  try {
    oee = JSON.parse(localStorage.getItem(LS_OEE_KEY) || '{}');
  } catch (e) {
    oee = {};
  }
  try {
    productivity = JSON.parse(localStorage.getItem(LS_PRODUCTIVITY_KEY) || '{}');
  } catch (e) {
    productivity = {};
  }
  if (!Object.keys(oee).length && localStorage.getItem('target1')) {
    oee = parseMonthlyTargets(localStorage.getItem('target1'));
  }
  if (!Object.keys(productivity).length && localStorage.getItem('target1')) {
    const legacy = parseLegacyScalar(localStorage.getItem('target1').split(',')[1], DEFAULT_MONTHLY_TARGET);
    productivity = parseMonthlyTargets(legacy);
  }
  return { oee, productivity };
}
